// CallTimerService.js - FIXED VERSION
class CallTimerService {
  constructor(firestoreService) {
    this.firestoreService = firestoreService;
    this.timerInterval = null;
    this.currentTimer = null;
    this.onTimerUpdate = null;
    this.onTimerExpired = null;
    this.isInitialized = false;
  }

  // FIXED: Initialize timer from Firebase with proper pause logic
  async initializeTimer() {
    try {
      console.log('⏰ Initializing call timer...');
      this.currentTimer = await this.firestoreService.getCallTimer();
      this.isInitialized = true;
      
      console.log('✅ Timer initialized:', this.currentTimer);
      
      // 🔥 CRITICAL FIX: Only calculate elapsed time if timer was ACTUALLY ACTIVE
      // If isActive is false, it means the timer was properly paused
      if (this.currentTimer.isActive && this.currentTimer.sessionStartTime) {
        console.log('⏰ Timer was active when user left - calculating elapsed time');
        
        const now = new Date();
        const sessionStart = new Date(this.currentTimer.sessionStartTime);
        const elapsedSeconds = Math.floor((now - sessionStart) / 1000);
        
        // Update remaining time based on elapsed time
        const newRemainingTime = Math.max(0, this.currentTimer.remainingTime - elapsedSeconds);
        
        if (newRemainingTime <= 0) {
          console.log('⏰ Timer expired while user was away');
          await this.handleTimerExpired();
        } else {
          console.log(`⏰ Timer resumed: ${newRemainingTime}s remaining (${elapsedSeconds}s elapsed)`);
          this.currentTimer.remainingTime = newRemainingTime;
          await this.saveTimer();
        }
      } else if (!this.currentTimer.isActive && this.currentTimer.lastPaused) {
        // 🔥 NEW: Timer was properly paused - preserve remaining time
        console.log('⏸️ Timer was paused when user left - preserving remaining time:', this.currentTimer.remainingTime);
        
        // Optional: Reset session start time to null since it shouldn't exist for paused timers
        if (this.currentTimer.sessionStartTime) {
          console.log('🧹 Cleaning up sessionStartTime for paused timer');
          this.currentTimer.sessionStartTime = null;
          await this.saveTimer();
        }
      } else if (!this.currentTimer.isActive && !this.currentTimer.lastPaused && this.currentTimer.remainingTime < 300) {
        // 🔥 NEW: Handle edge case - timer not active, not paused, but has less than full time
        // This might be a user returning after a long time - give them a fresh start
        console.log('🔄 Inactive timer with partial time - resetting for fresh start');
        return await this.resetTimer();
      }
      
      return this.currentTimer;
    } catch (error) {
      console.error('❌ Failed to initialize timer:', error);
      // Fallback to default timer
      this.currentTimer = {
        totalTime: 1200,
        remainingTime: 1200,
        lastPaused: null,
        isActive: false,
        sessionStartTime: null,
        resetCount: 0,
        lastResetBy: null,
        lastResetAt: null
      };
      return this.currentTimer;
    }
  }

  // IMPROVED: Start the timer with better state management
  async startTimer() {
    if (!this.isInitialized) {
      await this.initializeTimer();
    }

    if (this.currentTimer.remainingTime <= 0) {
      console.log('⏰ Cannot start - timer expired');
      if (this.onTimerExpired) this.onTimerExpired();
      return false;
    }

    console.log('⏰ Starting timer with', this.currentTimer.remainingTime, 'seconds remaining');
    
    this.currentTimer.isActive = true;
    this.currentTimer.sessionStartTime = new Date().toISOString();
    this.currentTimer.lastPaused = null; // Clear pause timestamp when starting
    
    await this.saveTimer();

    // Clear any existing interval
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    // Start countdown
    this.timerInterval = setInterval(async () => {
      if (this.currentTimer.remainingTime > 0) {
        this.currentTimer.remainingTime--;
        
        // Update callback for UI
        if (this.onTimerUpdate) {
          this.onTimerUpdate(this.currentTimer.remainingTime);
        }
        
        // Save to Firebase every 10 seconds to reduce writes
        if (this.currentTimer.remainingTime % 10 === 0) {
          await this.saveTimer();
        }
        
        // Check if expired
        if (this.currentTimer.remainingTime <= 0) {
          await this.handleTimerExpired();
        }
      }
    }, 1000);

    return true;
  }

  // IMPROVED: Pause the timer with proper state cleanup
  async pauseTimer() {
    if (!this.currentTimer.isActive) return;

    console.log('⏸️ Pausing timer at', this.currentTimer.remainingTime, 'seconds');
    
    this.currentTimer.isActive = false;
    this.currentTimer.lastPaused = new Date().toISOString();
    this.currentTimer.sessionStartTime = null; // 🔥 IMPORTANT: Clear session start when pausing
    
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    
    await this.saveTimer();
  }

  // Reset timer (user or admin)
  async resetTimer(adminUserId = null) {
    console.log('🔄 Resetting timer...', adminUserId ? `by admin: ${adminUserId}` : 'by user');
    
    // Stop current timer
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    
    // Reset timer data
    this.currentTimer = await this.firestoreService.resetCallTimer(adminUserId);
    
    // Update UI
    if (this.onTimerUpdate) {
      this.onTimerUpdate(this.currentTimer.remainingTime);
    }
    
    console.log('✅ Timer reset to', this.currentTimer.remainingTime, 'seconds');
    return this.currentTimer;
  }

  // Handle timer expiration
  async handleTimerExpired() {
    console.log('⏰ Timer expired!');
    
    this.currentTimer.isActive = false;
    this.currentTimer.remainingTime = 0;
    this.currentTimer.sessionStartTime = null;
    this.currentTimer.lastPaused = new Date().toISOString(); // Mark when it expired
    
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    
    await this.saveTimer();
    
    if (this.onTimerExpired) {
      this.onTimerExpired();
    }
  }

  // Save timer state to Firebase
  async saveTimer() {
    try {
      await this.firestoreService.updateCallTimer(this.currentTimer);
    } catch (error) {
      console.error('❌ Failed to save timer:', error);
    }
  }

  // Get current timer state
  getTimerState() {
    return this.currentTimer;
  }

  // Set callbacks
  setTimerUpdateCallback(callback) {
    this.onTimerUpdate = callback;
  }

  setTimerExpiredCallback(callback) {
    this.onTimerExpired = callback;
  }

  // Admin function to reset any user's timer
  async adminResetUserTimer(targetUserId, adminUserId) {
    try {
      const resetData = await this.firestoreService.adminResetUserTimer(targetUserId, adminUserId);
      console.log('🔄 Admin reset successful for user:', targetUserId);
      return resetData;
    } catch (error) {
      console.error('❌ Admin reset failed:', error);
      throw error;
    }
  }

  // IMPROVED: Cleanup with better state management
  cleanup() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    
    // 🔥 IMPORTANT: Pause timer when cleaning up (app closing)
    if (this.currentTimer && this.currentTimer.isActive) {
      // Don't await this to avoid blocking app cleanup
      this.pauseTimer().catch(error => {
        console.error('Failed to pause timer during cleanup:', error);
      });
    }
  }

  // Get formatted time string
  formatTime(seconds) {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }

  // Check if timer is expired
  isExpired() {
    return this.currentTimer && this.currentTimer.remainingTime <= 0;
  }

  // Check if timer is active
  isActive() {
    return this.currentTimer && this.currentTimer.isActive;
  }

  // Get remaining time in different formats
  getRemainingTime() {
    return this.currentTimer ? this.currentTimer.remainingTime : 1200;
  }

  getRemainingTimeFormatted() {
    return this.formatTime(this.getRemainingTime());
  }

  // Get timer status for UI
  getTimerStatus() {
    const remaining = this.getRemainingTime();
    
    if (remaining <= 0) return 'expired';
    if (remaining <= 30) return 'critical';
    if (remaining <= 60) return 'warning';
    return 'normal';
  }

  // Force sync with Firebase (useful for debugging)
  async forceSync() {
    if (!this.isInitialized) return;
    
    try {
      const latestTimer = await this.firestoreService.getCallTimer();
      this.currentTimer = latestTimer;
      
      if (this.onTimerUpdate) {
        this.onTimerUpdate(this.currentTimer.remainingTime);
      }
      
      console.log('🔄 Timer synced from Firebase:', latestTimer);
      return latestTimer;
    } catch (error) {
      console.error('❌ Failed to sync timer:', error);
    }
  }

  // Add time (admin only - for testing or special cases)
  async addTime(seconds, adminUserId) {
    if (!this.currentTimer) return false;
    
    try {
      const newRemainingTime = Math.min(
        this.currentTimer.totalTime, // Don't exceed total time
        this.currentTimer.remainingTime + seconds
      );
      
      this.currentTimer.remainingTime = newRemainingTime;
      this.currentTimer.lastResetBy = adminUserId;
      this.currentTimer.lastResetAt = new Date().toISOString();
      
      await this.saveTimer();
      
      if (this.onTimerUpdate) {
        this.onTimerUpdate(this.currentTimer.remainingTime);
      }
      
      console.log(`⏰ Added ${seconds}s to timer by admin: ${adminUserId}`);
      return true;
    } catch (error) {
      console.error('❌ Failed to add time:', error);
      return false;
    }
  }

  // NEW: Debug method to check timer state
  debugTimerState() {
    const state = {
      isInitialized: this.isInitialized,
      currentTimer: this.currentTimer,
      hasInterval: !!this.timerInterval,
      isActive: this.isActive(),
      isExpired: this.isExpired(),
      remainingTime: this.getRemainingTime(),
      status: this.getTimerStatus()
    };
    
    console.log('🔍 Timer Debug State:', state);
    return state;
  }
}

export default CallTimerService;